import {
  DeesElement,
  type TemplateResult,
  customElement,
  property,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { demoFunc } from './dees-grouped-list.demo.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-input/dees-input-toggle/dees-input-toggle.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-grouped-list': DeesGroupedList;
  }
}

/** A single row in a grouped list (inset-grouped "table view" cell). */
export interface IGroupedListRow {
  key?: string;
  label: string;
  /** Trailing value text (secondary). */
  value?: string;
  /** Leading icon (e.g. "lucide:bell"). */
  icon?: string;
  /** If set, the leading icon sits in a colored rounded tile (settings style). */
  iconColor?: string;
  /** If defined, renders a trailing toggle reflecting this value. */
  toggle?: boolean;
  /** Show a trailing chevron + hover affordance (navigation row). */
  navigates?: boolean;
  /** Render the label in the destructive (systemRed) color. */
  destructive?: boolean;
  disabled?: boolean;
  action?: () => void | Promise<void>;
}

/** A section: optional uppercase header, an inset rounded card of rows, optional footnote. */
export interface IGroupedListSection {
  header?: string;
  footer?: string;
  rows: IGroupedListRow[];
}

/**
 * dees-grouped-list — an inset-grouped list (settings pattern).
 * Sections render as inset rounded cards with hairline-separated rows; the
 * separators inset past the leading icon, exactly like a UITableView
 * insetGrouped style. Sits on a grouped/recessed background so the cards read
 * as elevated.
 */
@customElement('dees-grouped-list')
export class DeesGroupedList extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Array })
  accessor sections: IGroupedListSection[] = [];

  /** Inset (side margins) vs full-width grouped. Default inset. */
  @property({ type: Boolean, reflect: true })
  accessor inset: boolean = true;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-primary);
      }

      .list {
        display: flex;
        flex-direction: column;
      }

      :host([inset]) .list {
        padding: 0 var(--dees-spacing-lg);
      }

      /* Section header — uppercase, secondary, aligned to the card's text inset */
      .section-header {
        padding: 0 var(--dees-spacing-lg);
        margin: var(--dees-spacing-xl) 0 var(--dees-spacing-xs);
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--dees-color-text-muted);
      }
      .section:first-of-type .section-header {
        margin-top: var(--dees-spacing-sm);
      }

      /* Footnote help text under a section */
      .section-footer {
        padding: 0 var(--dees-spacing-lg);
        margin: var(--dees-spacing-xs) 0 0;
        font-size: var(--dees-font-footnote-size);
        line-height: var(--dees-font-footnote-line);
        letter-spacing: var(--dees-font-footnote-tracking);
        color: var(--dees-color-text-muted);
      }

      /* Inset rounded card */
      .card {
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        overflow: hidden;
      }

      .row {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        min-height: 44px;
        padding: var(--dees-spacing-sm) var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-primary);
        background: var(--dees-color-bg-primary);
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .row.navigates {
        cursor: pointer;
      }
      .row.navigates:hover {
        background: var(--dees-color-hover);
      }
      .row.navigates:active {
        background: var(--dees-color-active);
      }

      .row.disabled {
        opacity: 0.5;
        pointer-events: none;
      }

      .row.destructive .label {
        color: var(--dees-color-text-error);
      }

      /* Hairline separator that insets past the leading icon (settings style) */
      .row:not(:last-child)::after {
        content: '';
        position: absolute;
        left: var(--dees-spacing-lg);
        right: 0;
        bottom: 0;
        height: 1px;
        background: var(--dees-color-border-subtle);
      }
      .row.has-icon:not(:last-child)::after {
        left: calc(var(--dees-spacing-lg) + 28px + var(--dees-spacing-md));
      }

      /* Leading icon (fixed box so separators inset consistently) */
      .leading {
        width: 28px;
        height: 28px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--dees-color-text-secondary);
      }
      .leading.tile {
        border-radius: var(--dees-radius-sm);
      }
      .leading.tile dees-icon {
        color: var(--dees-color-on-accent);
      }
      .leading dees-icon {
        font-size: 17px;
      }
      .leading.tile dees-icon {
        font-size: 15px;
      }

      .label {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .value {
        margin-left: auto;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
      }

      .chevron {
        flex-shrink: 0;
        color: var(--dees-color-label-quaternary);
        font-size: 16px;
      }
      /* When both value and chevron are present, value shouldn't push via auto-margin twice */
      .value + .chevron {
        margin-left: var(--dees-spacing-xs);
      }

      .trailing-control {
        margin-left: auto;
        flex-shrink: 0;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="list">
        ${this.sections.map(
          (section) => html`
            <div class="section">
              ${section.header ? html`<div class="section-header">${section.header}</div>` : ''}
              <div class="card">
                ${section.rows.map((row) => this.renderRow(row))}
              </div>
              ${section.footer ? html`<div class="section-footer">${section.footer}</div>` : ''}
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderRow(row: IGroupedListRow): TemplateResult {
    const interactive = !!(row.navigates || row.action) && !row.disabled;
    return html`
      <div
        class="row ${row.icon ? 'has-icon' : ''} ${interactive ? 'navigates' : ''} ${row.destructive ? 'destructive' : ''} ${row.disabled ? 'disabled' : ''}"
        @click=${interactive ? () => this.handleRowClick(row) : undefined}
      >
        ${row.icon
          ? html`<span class="leading ${row.iconColor ? 'tile' : ''}" style=${row.iconColor ? `background:${row.iconColor}` : ''}>
              <dees-icon .icon=${row.icon}></dees-icon>
            </span>`
          : ''}
        <span class="label">${row.label}</span>
        ${row.toggle !== undefined
          ? html`<dees-input-toggle
              class="trailing-control"
              .value=${row.toggle}
              @newValue=${(e: CustomEvent) => this.handleToggle(row, e)}
            ></dees-input-toggle>`
          : html`
              ${row.value ? html`<span class="value">${row.value}</span>` : ''}
              ${row.navigates ? html`<dees-icon class="chevron" .icon=${'lucide:chevron-right'}></dees-icon>` : ''}
            `}
      </div>
    `;
  }

  private async handleRowClick(row: IGroupedListRow): Promise<void> {
    if (row.action) await row.action();
    this.dispatchEvent(
      new CustomEvent('rowSelect', { detail: { row }, bubbles: true, composed: true }),
    );
  }

  private handleToggle(row: IGroupedListRow, ev: CustomEvent): void {
    const value = ev.detail as boolean;
    row.toggle = value;
    this.dispatchEvent(
      new CustomEvent('rowToggle', { detail: { row, value }, bubbles: true, composed: true }),
    );
  }
}
